import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { getUploadList } from 'api/uploads';
import { NON_SPECIFIC_STUDY_VALUES } from 'components/SearchFilters/hooks/useMainFilters';
import {
  getFileInfoSlideIntakeEvidence,
  getSlideIntakeLifecycle,
  SlideIntakeLifecyclePresentation,
  SlideIntakeLifecycleState,
} from 'components/SlideIntake/lifecycle';
import { getTranslucentSurfaceStyles } from 'components/theme/theme';
import { getUploadFilterForLifecycle } from 'components/UploadsDashboard/helpers';
import { uniqBy } from 'es-toolkit/compat';
import { FileInfo } from 'interfaces/fileList';
import { compact, concat, countBy, filter, find, includes, map, size, toString } from 'lodash';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BooleanParam, useQueryParam } from 'use-query-params';
import { useStudyUploadList } from 'utils/queryHooks/useStudyUploadList';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { getOrganizationEntryUrl } from './CaseManifest/organizationEntry';
import { getDatasetMode } from './ProceduresPage/datasetMode';
import StudyHeaderActionBadge from './StudyHeaderActionBadge';

interface StudyUploadsStatusProps {
  navigateToUploadsDashboard?: boolean;
}

interface ActionableUploadGroup extends SlideIntakeLifecyclePresentation {
  count: number;
}

const actionableLifecycleOrder: SlideIntakeLifecycleState[] = [
  'ingestFailed',
  'rejectedIngest',
  'needsAttention',
  'needsCaseAssignment',
  'ingesting',
  'queuedForIngest',
];

export const getNeedsCaseAssignmentSlideIds = (fileList?: Awaited<ReturnType<typeof getUploadList>>) =>
  compact(
    map(
      uniqBy(
        concat(fileList?.slides?.pending || [], fileList?.slides?.processed || []),
        (row) => row.objectKey || row.id
      ),
      (row) =>
        getSlideIntakeLifecycle(getFileInfoSlideIntakeEvidence(row)).state === 'needsCaseAssignment' && row.slideId
          ? String(row.slideId)
          : null
    )
  );

export const getActionableUploadGroups = (
  fileList?: Awaited<ReturnType<typeof getUploadList>>
): ActionableUploadGroup[] => {
  // Counts are uploaded files, so an object key listed more than once is counted once.
  const uploadedFiles = uniqBy(
    concat(fileList?.slides?.pending || [], fileList?.slides?.processed || []),
    (row) => row.objectKey || row.id
  );
  const presentations = map(uploadedFiles, (row) => getSlideIntakeLifecycle(getFileInfoSlideIntakeEvidence(row)));
  const lifecycleCounts = countBy(presentations, 'state');

  return compact(
    map(actionableLifecycleOrder, (state) => {
      const count = lifecycleCounts[state] || 0;
      if (!count) return null;

      const presentation = find(presentations, (candidate) => candidate.state === state);
      return presentation ? { ...presentation, count } : null;
    })
  );
};

export const getIngestingUploadCount = (fileList?: Awaited<ReturnType<typeof getUploadList>>) =>
  find(getActionableUploadGroups(fileList), (group) => group.state === 'ingesting')?.count || 0;

const hasCaseAssignment = (row: FileInfo) => size(row.cases || []) > 0;

export const getStudyUploadProgress = (fileList?: Awaited<ReturnType<typeof getUploadList>>) => {
  const slideRows = concat(fileList?.slides?.pending || [], fileList?.slides?.processed || []);
  const ingestedRows = filter(
    slideRows,
    (row) => !row.latestSuccessfulIngestJobArchived && (Boolean(row.slideId) || row.status === 'completed')
  );

  return {
    uploadedSlideCount: size(slideRows),
    activeIngestCount: size(filter(slideRows, (row) => Boolean(row.activeIngestJob?.id))),
    ingestedSlideCount: size(ingestedRows),
    groupedIntoCasesCount: size(filter(ingestedRows, (row) => hasCaseAssignment(row))),
  };
};

export const getUploadIngestStatusSummary = (fileList?: Awaited<ReturnType<typeof getUploadList>>) => {
  const slideRows = concat(fileList?.slides?.pending || [], fileList?.slides?.processed || []);
  const visibleFileRows = filter(fileList?.all || [], (row) => !row.isHidden);
  const manifestRows = filter(visibleFileRows, (row) => row.isManifest);
  const otherFileRows = filter(visibleFileRows, (row) => !row.isManifest && !row.uploadKey);
  const dashboardUploadCount = size(slideRows) + size(manifestRows) + size(otherFileRows);
  const slideUploadCount = size(slideRows);
  const manifestCount = size(manifestRows);
  const otherFileCount = size(otherFileRows);
  const lifecycleCounts = countBy(
    slideRows,
    (row): SlideIntakeLifecycleState => getSlideIntakeLifecycle(getFileInfoSlideIntakeEvidence(row)).state
  );
  const getLifecycleCount = (state: SlideIntakeLifecycleState) => lifecycleCounts[state] || 0;
  const assignedCount = getLifecycleCount('assignedToCase');
  const allUploadsComplete = slideUploadCount > 0 && assignedCount === slideUploadCount;
  const summaryParts = compact([
    getLifecycleCount('ingesting') ? `${getLifecycleCount('ingesting')} ingesting` : null,
    getLifecycleCount('ingestFailed') ? `${getLifecycleCount('ingestFailed')} ingest failed` : null,
    getLifecycleCount('rejectedIngest')
      ? `${getLifecycleCount('rejectedIngest')} rejected ${
          getLifecycleCount('rejectedIngest') === 1 ? 'ingest' : 'ingests'
        }`
      : null,
    assignedCount ? `${assignedCount} assigned to case` : null,
    getLifecycleCount('needsCaseAssignment')
      ? `${getLifecycleCount('needsCaseAssignment')} ${
          getLifecycleCount('needsCaseAssignment') === 1 ? 'needs' : 'need'
        } case assignment`
      : null,
    getLifecycleCount('queuedForIngest') ? `${getLifecycleCount('queuedForIngest')} queued for ingest` : null,
    manifestCount ? `${manifestCount} manifest ${manifestCount === 1 ? 'file' : 'files'}` : null,
    otherFileCount ? `${otherFileCount} other ${otherFileCount === 1 ? 'file' : 'files'}` : null,
  ]);

  return {
    allUploadsComplete,
    dashboardUploadCount,
    slideUploadCount,
    summaryText: summaryParts.length ? summaryParts.join(' · ') : 'No uploads',
  };
};

const StudyUploadsStatus = ({ navigateToUploadsDashboard = false }: StudyUploadsStatusProps) => {
  const [popoverAnchorElement, setPopoverAnchorElement] = useState<HTMLElement | null>(null);
  const { labId } = useCurrentLabId();
  const { encodedFilters, queryParams } = useEncodedFilters();
  const [pendingSlidesMode] = useQueryParam('pendingSlidesMode', BooleanParam);
  const selectedStudyId = toString(queryParams.filters?.studyId);
  const hasSelectedStudy =
    Boolean(queryParams.filters?.studyId) && !includes(NON_SPECIFIC_STUDY_VALUES, selectedStudyId);

  const {
    data: fileList,
    isLoading,
    isFetching,
    isError,
  } = useStudyUploadList({ studyId: selectedStudyId, enabled: hasSelectedStudy });

  const { dashboardUploadCount, summaryText } = getUploadIngestStatusSummary(fileList);
  const actionableUploadGroups = getActionableUploadGroups(fileList);
  const actionableUploadCount = actionableUploadGroups.reduce((total, group) => total + group.count, 0);
  const actionableUploadSummary = map(
    actionableUploadGroups,
    (group) => `${group.count} ${group.label.toLocaleLowerCase()}`
  ).join(' · ');
  const enableNavigation = navigateToUploadsDashboard;
  const isLoadingUploads = isLoading || (isFetching && typeof fileList === 'undefined');
  const isRefreshingUploads = isFetching && !isLoadingUploads;
  const useSecondaryBadge =
    typeof fileList !== 'undefined' && size(fileList.slides?.pending) === 0 && size(fileList.slides?.failed) === 0;
  const shouldRender = enableNavigation || dashboardUploadCount > 0;
  const uploadsDashboardLocation = {
    pathname: `/uploads-dashboard`,
    search: encodedFilters,
  };
  const getActionableUploadLocation = (group: ActionableUploadGroup) => {
    if (group.state === 'needsCaseAssignment') {
      return getOrganizationEntryUrl({
        studyId: selectedStudyId,
        labId,
        manifestState: {
          manualManifestSlideIdsToLoad: getNeedsCaseAssignmentSlideIds(fileList),
          showOnlyUnassignedSlides: true,
        },
        returnTo: {
          origin: 'dataset',
          labId,
          studyId: selectedStudyId,
          datasetMode: getDatasetMode({ slidesMode: queryParams.slidesMode, pendingSlidesMode }),
          page: queryParams.page,
          searchTerm: queryParams.searchTerm || undefined,
        },
      });
    }

    const searchParams = new URLSearchParams(encodedFilters);
    searchParams.set('uploadFilters', getUploadFilterForLifecycle(group));
    return { pathname: '/uploads-dashboard', search: searchParams.toString() };
  };

  if (!hasSelectedStudy) {
    return null;
  }

  // Hidden uploads are only reachable (and restorable) through Review uploads, and a failed refresh must keep the stale-status warning.
  const hasNoUploads = dashboardUploadCount === 0 && size(fileList?.all) === 0;

  if (enableNavigation && typeof fileList !== 'undefined' && !isLoadingUploads && !isError && hasNoUploads) {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', marginRight: '10px' }} aria-live="polite">
        <Tooltip title="Press Add Slides for upload instructions" placement="bottom" arrow>
          <Chip
            label="No uploads"
            variant="outlined"
            sx={{ borderRadius: '100px', fontSize: '14px', fontWeight: 'bold', height: '40px', cursor: 'default' }}
          />
        </Tooltip>
      </Box>
    );
  }

  if (enableNavigation) {
    const unavailableLifecycle = getSlideIntakeLifecycle({ requestState: 'error', fileKind: 'slide' });
    const popoverId = 'study-actionable-uploads-popover';
    const isPopoverOpen = Boolean(popoverAnchorElement);

    return (
      <ClickAwayListener onClickAway={() => setPopoverAnchorElement(null)}>
        <Box
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, marginRight: '10px' }}
          aria-live="polite"
          onMouseLeave={() => setPopoverAnchorElement(null)}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) {
              setPopoverAnchorElement(null);
            }
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              setPopoverAnchorElement(null);
              popoverAnchorElement?.focus();
            }
          }}
        >
          <StudyHeaderActionBadge
            color={isLoadingUploads || isRefreshingUploads || useSecondaryBadge ? 'secondary' : 'primary'}
            count={actionableUploadCount}
            countAriaLabel={`${actionableUploadCount} ${
              actionableUploadCount === 1 ? 'upload needs' : 'uploads need'
            } attention`}
            countTooltip={actionableUploadSummary}
            isLoading={isLoadingUploads || isRefreshingUploads}
            loadingAriaLabel="Loading uploads count"
          >
            <span
              onMouseEnter={(event) => setPopoverAnchorElement(event.currentTarget)}
              onFocus={(event) => setPopoverAnchorElement(event.currentTarget)}
            >
              <Button
                component={Link}
                to={uploadsDashboardLocation}
                aria-controls={isPopoverOpen ? popoverId : undefined}
                aria-expanded={isPopoverOpen}
                aria-haspopup="dialog"
                aria-label="Review uploads"
                color="secondary"
                variant="outlined"
                size="small"
                onClick={() => setPopoverAnchorElement(null)}
                sx={{
                  borderRadius: '100px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  height: '40px',
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                Review uploads
              </Button>
            </span>
          </StudyHeaderActionBadge>
          <Popper
            id={popoverId}
            open={isPopoverOpen}
            anchorEl={popoverAnchorElement}
            placement="bottom-end"
            disablePortal
            sx={{ zIndex: (theme) => theme.zIndex.modal }}
          >
            <Paper
              role="dialog"
              aria-label={actionableUploadCount > 0 ? 'Actionable uploads' : 'Uploads'}
              sx={(theme) => ({
                ...getTranslucentSurfaceStyles(theme),
                width: 360,
                maxWidth: `calc(100vw - ${theme.spacing(3)})`,
                mt: 1,
                overflow: 'hidden',
              })}
            >
              {isLoadingUploads || isError || actionableUploadCount === 0 ? (
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {isLoadingUploads
                      ? 'Loading uploads...'
                      : isError && typeof fileList === 'undefined'
                      ? unavailableLifecycle.explanation
                      : isError
                      ? 'Status may be out of date.'
                      : 'No uploads need attention.'}
                  </Typography>
                </Box>
              ) : null}
              {actionableUploadGroups.length > 0 ? (
                <List disablePadding>
                  {map(actionableUploadGroups, (group) => (
                    <ListItemButton
                      key={group.state}
                      component={Link}
                      to={getActionableUploadLocation(group)}
                      onClick={() => setPopoverAnchorElement(null)}
                      sx={{ px: 2, py: 1 }}
                    >
                      <ListItemText
                        primary={`${group.count} ${group.label}`}
                        primaryTypographyProps={{ fontWeight: 700 }}
                      />
                      <ArrowForwardRoundedIcon color="action" fontSize="small" aria-hidden />
                    </ListItemButton>
                  ))}
                </List>
              ) : null}
              <Divider />
              <Box sx={{ px: 1, py: 0.75, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  component={Link}
                  to={uploadsDashboardLocation}
                  onClick={() => setPopoverAnchorElement(null)}
                  size="small"
                >
                  View all uploads
                </Button>
              </Box>
            </Paper>
          </Popper>
        </Box>
      </ClickAwayListener>
    );
  }

  if (isLoadingUploads) {
    return (
      <Box sx={{ marginRight: '10px' }} aria-live="polite">
        <Typography
          component="span"
          color="text.secondary"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}
        >
          <CircularProgress size={14} color="inherit" />
          Loading uploaded files...
        </Typography>
      </Box>
    );
  }

  if (isError && typeof fileList === 'undefined') {
    const unavailableLifecycle = getSlideIntakeLifecycle({ requestState: 'error', fileKind: 'slide' });

    return (
      <Box sx={{ marginRight: '10px' }} aria-live="polite">
        <Typography component="span" color={unavailableLifecycle.tone}>
          {unavailableLifecycle.label}
        </Typography>
      </Box>
    );
  }

  return shouldRender ? (
    <Box sx={{ marginRight: '10px' }} aria-live="polite">
      <Typography
        component="span"
        color="text.secondary"
        style={{ textDecoration: 'none' }}
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}
      >
        {summaryText}
        {isError ? ' · Status changed' : null}
        {isRefreshingUploads ? <CircularProgress size={14} color="inherit" /> : null}
      </Typography>
    </Box>
  ) : null;
};

export default StudyUploadsStatus;
